import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { trackEvent, EVENTS } from '../lib/analytics';
import { billingApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePlan } from '../hooks/usePlan';
import {
  Check,
  X,
  Zap,
  Package,
  Users,
  Wrench,
  ClipboardCheck,
  Award,
  Shield,
  BarChart3,
  Smartphone,
  QrCode,
  Radio,
  Mail,
  Headphones,
  Loader2
} from 'lucide-react';

const features = [
  { name: 'Assets', free: 'Up to 10', pro: 'Unlimited', icon: Package },
  { name: 'Office Users (Admin/Manager)', free: '1 admin', pro: 'Unlimited ($39/user/mo)', icon: Users },
  { name: 'Field / Mobile Users', free: false, pro: 'Unlimited (free)', icon: Smartphone },
  { name: 'Work Orders', free: '10/month', pro: 'Unlimited', icon: Wrench },
  { name: 'Inspections', free: '10/month', pro: 'Unlimited', icon: ClipboardCheck },
  { name: 'Certificates', free: 'Up to 5', pro: 'Unlimited', icon: Award },
  { name: 'Task Library', free: '3 templates', pro: 'Unlimited', icon: ClipboardCheck },
  { name: 'Mobile Workshop Mode', free: false, pro: true, icon: Smartphone },
  { name: 'Public QR + Anonymous Reporting', free: false, pro: true, icon: QrCode },
  { name: 'Telemetry Integration', free: false, pro: true, icon: Radio },
  { name: 'Insights Dashboard', free: '30 days', pro: 'Full history + export', icon: BarChart3 },
  { name: 'Compliance Standards', free: true, pro: true, icon: Shield },
  { name: 'Email Notifications', free: false, pro: true, icon: Mail },
  { name: 'Priority Support', free: false, pro: true, icon: Headphones },
];

const ValueCell = ({ value }) => {
  if (value === true) return <Check className="w-5 h-5 text-green-400 mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-gray-600 mx-auto" />;
  return <span className="text-sm">{value}</span>;
};

export const PricingPage = () => {
  const { isAdmin } = useAuth();
  const { isPro } = usePlan();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!isAdmin()) {
      toast.error('Only an admin can upgrade your workspace. Please ask your admin.');
      return;
    }
    setCheckoutLoading(true);
    try {
      trackEvent(EVENTS.UPGRADE_CLICKED, { location: 'pricing_page', method: 'stripe_checkout' });
      const res = await billingApi.checkout(window.location.origin);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('No checkout URL returned');
        setCheckoutLoading(false);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured stripe_not_configured payload → show a friendly fallback
      // with a mailto so the user has somewhere to go, rather than a vague
      // technical error.
      if (detail && typeof detail === 'object' && detail.code === 'stripe_not_configured') {
        toast.error(detail.message, {
          duration: 10000,
          action: {
            label: 'Email sales',
            onClick: () => { window.location.href = 'mailto:sales@equiprsoftware.com?subject=Pro%20upgrade%20enquiry'; },
          },
        });
        // Log the diagnostic so admins debugging deploys see exactly which keys
        // are missing — never shown to the end user.
        // eslint-disable-next-line no-console
        console.warn('[billing] Stripe not configured — missing env vars:', detail.missing_env_vars);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Checkout failed — please try again or contact sales.');
      }
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase tracking-tight">
          Simple, Transparent Pricing
        </h1>
        <p className="text-muted-foreground text-sm mt-2">
          Start and stay free — up to 10 assets. Upgrade when you're ready.
        </p>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Free Plan */}
        <Card className="relative">
          <CardContent className="p-8">
            <div className="mb-6">
              <h2 className="font-heading text-xl font-bold">Free</h2>
              <p className="text-muted-foreground text-sm mt-1">For small teams getting started</p>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-mono font-bold">$0</span>
              <span className="text-muted-foreground text-sm ml-1">/forever</span>
            </div>
            <ul className="space-y-3 mb-8">
              {['Up to 10 assets', '1 admin user', '10 work orders/month', '10 inspections/month', '5 certificates', '3 task templates', 'Compliance standards'].map(f => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full" disabled data-testid="free-current-plan-btn">
              {isPro ? 'Free Plan' : 'Current Plan'}
            </Button>
          </CardContent>
        </Card>

        {/* Professional Plan */}
        <Card className="relative border-[#FFD108] border-2">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-[#FFD108] text-black font-semibold px-3 py-1">Recommended</Badge>
          </div>
          <CardContent className="p-8">
            <div className="mb-6">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                Professional
                <Zap className="w-5 h-5 text-[#FFD108]" />
              </h2>
              <p className="text-muted-foreground text-sm mt-1">For growing fleets and teams</p>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-mono font-bold text-[#FFD108]">$39</span>
              <span className="text-muted-foreground text-sm ml-1">/office user/month</span>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              Field &amp; mobile users are <span className="text-[#FFD108] font-semibold">always free</span>
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'Unlimited assets & components',
                'Unlimited office users ($39/user/mo)',
                'Unlimited field & mobile users (free)',
                'Unlimited work orders & inspections',
                'Unlimited certificates',
                'Mobile Workshop Mode',
                'Public QR + anonymous reporting',
                'GPS/Telemetry integration',
                'Full Insights dashboard',
                'Email notifications & alerts',
                'Priority support',
              ].map(f => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#FFD108] shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              className="w-full btn-volt h-12"
              onClick={handleUpgrade}
              disabled={isPro || checkoutLoading}
              data-testid="upgrade-checkout-btn"
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirecting…
                </>
              ) : isPro ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Current Plan
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Upgrade to Professional
                </>
              )}
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-3">
              Secure checkout powered by Stripe. Billed monthly per office user — field &amp; mobile users are free.{' '}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => {
                  trackEvent(EVENTS.UPGRADE_CLICKED, { location: 'pricing_page', method: 'contact_sales' });
                  window.open('mailto:sales@equiprsoftware.com?subject=EQUIPR Guardian Volume Pricing', '_blank');
                }}
                data-testid="contact-sales-link"
              >
                Need volume pricing? Contact sales.
              </button>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Feature Comparison Table */}
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-lg font-semibold text-center mb-6">Feature Comparison</h2>
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 label-overline">Feature</th>
                  <th className="text-center p-4 label-overline w-[140px]">Free</th>
                  <th className="text-center p-4 label-overline w-[160px] text-[#FFD108]">Professional</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feat, i) => {
                  const Icon = feat.icon;
                  return (
                    <tr key={feat.name} className="border-b border-border last:border-0 hover:bg-secondary/30">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{feat.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center"><ValueCell value={feat.free} /></td>
                      <td className="p-4 text-center"><ValueCell value={feat.pro} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PricingPage;
